var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Brushaway from '@components/Brushaway';
import TextInput from '@components/TextInput/TextInput';
import Select from '@components/Select/Select';
import InputTextArea, { StyleVariant } from '@components/InputTextArea/InputTextArea';
import { Button, SelectInput } from '@ui';
import { translate } from '@utils/i18n';
import { buildParentOptionKey, mapAttachableToParentOption, mapComponentToParentOption } from '../mapper';
import { isTypeEligibleUnderParent } from '../utils/componentCompatibility';
import { resolveEffectiveLockedTypeId } from '../utils/resolveEffectiveLockedTypeId';
import DynamicSettings from './DynamicSettings';
import './AddComponentModalStyles.scss';
const namespace = 'components.add_modal';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Flatten all active components in the hierarchy into a single array. */
function flattenActiveComponents(comps) {
    return comps.reduce((acc, comp) => {
        if (comp.active)
            acc.push(comp);
        if (comp.children)
            acc.push(...flattenActiveComponents(comp.children));
        return acc;
    }, []);
}
/** Derive the default ParentOption for standalone (non-locked) mode. */
function resolveDefaultParent(parentOptions, fallbackAttachableId) {
    var _a;
    if (fallbackAttachableId != null) {
        const found = parentOptions.find((o) => o.kind === 'attachable' && o.attachableId === fallbackAttachableId);
        if (found)
            return found;
    }
    return (_a = parentOptions.find((o) => o.kind === 'attachable')) !== null && _a !== void 0 ? _a : null;
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AddComponentModal({ isOpen, onClose, components, componentTypes, componentAttachables, createComponent, isCreating, lockedParent = null, lockedTypeId = null, supportsSubcomponents = false }) {
    var _a, _b, _c, _d, _e;
    const isParentLocked = lockedParent != null;
    const flatComponents = useMemo(() => flattenActiveComponents(components), [components]);
    // Build the unified parent option list (used in searchable mode only)
    const parentOptions = useMemo(() => {
        const attachableOpts = componentAttachables.map(mapAttachableToParentOption);
        if (!supportsSubcomponents)
            return attachableOpts;
        const componentOpts = flatComponents.map(mapComponentToParentOption);
        return [...attachableOpts, ...componentOpts];
    }, [componentAttachables, flatComponents, supportsSubcomponents]);
    // Map from string key → ParentOption for fast lookup on SelectInput onChange
    const parentOptionsMap = useMemo(() => {
        const map = new Map();
        parentOptions.forEach((opt) => map.set(buildParentOptionKey(opt), opt));
        return map;
    }, [parentOptions]);
    // SelectInput-compatible option list (value = stable string key)
    const parentSelectOptions = useMemo(() => parentOptions.map((opt) => ({
        value: buildParentOptionKey(opt),
        label: opt.label,
        id: buildParentOptionKey(opt)
    })), [parentOptions]);
    // Initial parent: locked parent if set, otherwise first available attachable
    const defaultParent = useMemo(() => lockedParent !== null && lockedParent !== void 0 ? lockedParent : resolveDefaultParent(parentOptions, componentAttachables.length > 0 ? componentAttachables[0].attachableId : null), [lockedParent, parentOptions, componentAttachables]);
    const { control, register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            name: '',
            typeId: lockedTypeId !== null && lockedTypeId !== void 0 ? lockedTypeId : null,
            selectedParent: defaultParent,
            location: '',
            barcode: '',
            externalId: '',
            notes: '',
            settings: {}
        }
    });
    const watchedParent = watch('selectedParent');
    const watchedTypeId = watch('typeId');
    const selectedParentTypeId = useMemo(() => {
        var _a;
        if ((watchedParent === null || watchedParent === void 0 ? void 0 : watchedParent.kind) !== 'component')
            return null;
        const parentComponent = flatComponents.find((component) => component.id === watchedParent.componentId);
        return (_a = parentComponent === null || parentComponent === void 0 ? void 0 : parentComponent.typeId) !== null && _a !== void 0 ? _a : null;
    }, [watchedParent, flatComponents]);
    const compatibleChildTypeIds = useMemo(() => {
        var _a;
        if (selectedParentTypeId == null)
            return null;
        const parentType = componentTypes.find((type) => type.id === selectedParentTypeId);
        return (_a = parentType === null || parentType === void 0 ? void 0 : parentType.compatibleChildTypeIds) !== null && _a !== void 0 ? _a : [];
    }, [componentTypes, selectedParentTypeId]);
    const typeOptions = useMemo(() => {
        return componentTypes
            .filter((type) => {
            const isParentCompatible = compatibleChildTypeIds == null || compatibleChildTypeIds.includes(type.id);
            return isTypeEligibleUnderParent(type, watchedParent) && isParentCompatible;
        })
            .map((type) => ({
            label: type.typeName,
            value: type.id
        }));
    }, [componentTypes, watchedParent, compatibleChildTypeIds]);
    // IP-15178 Option 1: locked parent + exactly one compatible type → lock Type.
    // Explicit launch-context lockedTypeId always wins over the sole-option rule.
    const effectiveLockedTypeId = useMemo(() => resolveEffectiveLockedTypeId(lockedTypeId, isParentLocked, typeOptions.map((option) => Number(option.value))), [lockedTypeId, isParentLocked, typeOptions]);
    const isTypeLocked = effectiveLockedTypeId != null;
    // Clear selected type when it is no longer valid for the newly-selected parent (searchable mode only)
    useEffect(() => {
        if (isTypeLocked)
            return; // Do not auto-clear in locked-type mode
        if (!watchedTypeId || !watchedParent)
            return;
        const selectedType = componentTypes.find((t) => t.id === Number(watchedTypeId));
        if (!selectedType)
            return;
        const isAttachableCompatible = isTypeEligibleUnderParent(selectedType, watchedParent);
        const isParentCompatible = compatibleChildTypeIds == null || compatibleChildTypeIds.includes(selectedType.id);
        if (!isAttachableCompatible || !isParentCompatible) {
            setValue('typeId', null);
        }
    }, [watchedParent, watchedTypeId, componentTypes, setValue, isTypeLocked, compatibleChildTypeIds]);
    // Reset form only when modal transitions from closed → open
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            wasOpenRef.current = true;
            const newDefaultParent = lockedParent !== null && lockedParent !== void 0 ? lockedParent : resolveDefaultParent(parentOptions, componentAttachables.length > 0 ? componentAttachables[0].attachableId : null);
            reset({
                name: '',
                typeId: lockedTypeId !== null && lockedTypeId !== void 0 ? lockedTypeId : null,
                selectedParent: newDefaultParent,
                location: '',
                barcode: '',
                externalId: '',
                notes: '',
                settings: {}
            });
        }
        else if (!isOpen && wasOpenRef.current) {
            wasOpenRef.current = false;
        }
    }, [isOpen, reset, parentOptions, componentAttachables, lockedParent, lockedTypeId]);
    // Seed Type once Option 1 resolves a sole compatible type (or launch context locked it).
    useEffect(() => {
        if (!isOpen || effectiveLockedTypeId == null)
            return;
        if (Number(watchedTypeId) === effectiveLockedTypeId)
            return;
        setValue('typeId', effectiveLockedTypeId);
    }, [isOpen, effectiveLockedTypeId, watchedTypeId, setValue]);
    const onSubmit = (data) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield createComponent(data);
            onClose();
        }
        catch (error) {
            console.error(error);
        }
    });
    const handleButtonSubmit = () => {
        void handleSubmit(onSubmit)();
    };
    const handleFormSubmit = (e) => {
        void handleSubmit(onSubmit)(e);
    };
    const handleClose = () => {
        var _a;
        const fallback = (_a = lockedParent !== null && lockedParent !== void 0 ? lockedParent : parentOptions.find((o) => o.kind === 'attachable')) !== null && _a !== void 0 ? _a : null;
        reset({
            name: '',
            typeId: lockedTypeId !== null && lockedTypeId !== void 0 ? lockedTypeId : null,
            selectedParent: fallback,
            location: '',
            barcode: '',
            externalId: '',
            notes: '',
            settings: {}
        });
        onClose();
    };
    const selectedComponentType = componentTypes.find((type) => type.id === Number(watchedTypeId));
    const fieldDefinitions = ((_a = selectedComponentType === null || selectedComponentType === void 0 ? void 0 : selectedComponentType.fieldDefinitions) === null || _a === void 0 ? void 0 : _a.fields) || [];
    // Compatibility check for locked parent + locked type combination
    const lockedTypeObj = isTypeLocked ? componentTypes.find((t) => t.id === effectiveLockedTypeId) : null;
    const isLockedTypeIncompatible = isParentLocked &&
        isTypeLocked &&
        lockedTypeObj != null &&
        (!isTypeEligibleUnderParent(lockedTypeObj, lockedParent) ||
            (compatibleChildTypeIds != null && !compatibleChildTypeIds.includes(lockedTypeObj.id)));
    const isSubmitDisabled = isSubmitting || isCreating || isLockedTypeIncompatible;
    // Locked field values still render in disabled controls so users see the same
    // form shape as editable mode while preserving launch context.
    const lockedParentKey = isParentLocked ? buildParentOptionKey(lockedParent) : undefined;
    const lockedParentOptions = isParentLocked
        ? [
            {
                value: lockedParentKey,
                label: lockedParent.label,
                id: lockedParentKey
            }
        ]
        : [];
    const lockedTypeName = (_b = lockedTypeObj === null || lockedTypeObj === void 0 ? void 0 : lockedTypeObj.typeName) !== null && _b !== void 0 ? _b : (isTypeLocked ? translate('fallback_type_label', { namespace, typeId: String(effectiveLockedTypeId) }) : null);
    const lockedTypeOptions = isTypeLocked
        ? [
            {
                label: lockedTypeName !== null && lockedTypeName !== void 0 ? lockedTypeName : '',
                value: effectiveLockedTypeId
            }
        ]
        : [];
    return (React.createElement(Brushaway, { open: isOpen, title: translate('title', { namespace }), onClose: handleClose, isForm: true, customFooter: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: handleClose }, translate('cancel', { namespace })),
            React.createElement(Button, { variant: "primary", onClick: handleButtonSubmit, disabled: isSubmitDisabled }, isSubmitting || isCreating
                ? translate('creating_component', { namespace })
                : translate('create_component', { namespace }))) },
        React.createElement("form", { className: "add-component-form", onSubmit: handleFormSubmit },
            isParentLocked ? (React.createElement("div", { className: "form-row" },
                React.createElement(SelectInput, { label: translate('parent_label', { namespace }), required: true, disabled: true, value: lockedParentKey, options: lockedParentOptions, onChange: () => undefined, error: (_c = errors.selectedParent) === null || _c === void 0 ? void 0 : _c.message }))) : (React.createElement("div", { className: "form-row" },
                React.createElement(Controller, { name: "selectedParent", control: control, rules: { validate: (v) => v != null || translate('parent_required', { namespace }) }, render: ({ field }) => {
                        var _a;
                        return (React.createElement(SelectInput, { label: translate('parent_label', { namespace }), required: true, showSearch: true, value: field.value ? buildParentOptionKey(field.value) : undefined, options: parentSelectOptions, onChange: (key) => {
                                const option = parentOptionsMap.get(key);
                                field.onChange(option !== null && option !== void 0 ? option : null);
                            }, error: (_a = errors.selectedParent) === null || _a === void 0 ? void 0 : _a.message }));
                    } }))),
            isLockedTypeIncompatible && (React.createElement("div", { className: "form-row" },
                React.createElement("div", { className: "add-component-form__compat-error", role: "alert" },
                    React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
                    ' ',
                    translate('incompatible_type_message', { namespace, typeName: lockedTypeName !== null && lockedTypeName !== void 0 ? lockedTypeName : '' })))),
            isTypeLocked ? (React.createElement("div", { className: "form-row" },
                React.createElement(Select
                // eslint-disable-next-line react/jsx-props-no-spreading
                , Object.assign({}, register('typeId', { required: translate('type_required', { namespace }) }), { label: translate('type_label', { namespace }), options: lockedTypeOptions, required: true, disabled: true, error: (_d = errors.typeId) === null || _d === void 0 ? void 0 : _d.message })))) : (React.createElement("div", { className: "form-row" },
                React.createElement(Select
                // eslint-disable-next-line react/jsx-props-no-spreading
                , Object.assign({}, register('typeId', { required: translate('type_required', { namespace }) }), { label: translate('type_label', { namespace }), options: typeOptions, required: true, error: (_e = errors.typeId) === null || _e === void 0 ? void 0 : _e.message })))),
            React.createElement("div", { className: "form-row" },
                React.createElement(Controller, { name: "name", control: control, rules: { required: translate('component_name_required', { namespace }) }, render: ({ field }) => {
                        var _a;
                        return (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('component_name_label', { namespace }), error: (_a = errors.name) === null || _a === void 0 ? void 0 : _a.message, required: true }));
                    } })),
            React.createElement("div", { className: "form-row" },
                React.createElement(Controller, { name: "location", control: control, render: ({ field }) => (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('location_label', { namespace }) })) })),
            React.createElement("div", { className: "form-row" },
                React.createElement(Controller, { name: "notes", control: control, render: ({ field }) => (React.createElement(InputTextArea
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    , Object.assign({}, field, { label: translate('notes_label', { namespace }), variant: StyleVariant.Light, buttonControlled: false }))) })),
            React.createElement("div", { className: "form-row form-row--two-cols" },
                React.createElement(Controller, { name: "barcode", control: control, render: ({ field }) => (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('barcode_label', { namespace }) })) }),
                React.createElement(Controller, { name: "externalId", control: control, render: ({ field }) => (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('external_id_label', { namespace }) })) })),
            React.createElement(DynamicSettings, { fieldDefinitions: fieldDefinitions, control: control }))));
}
